// Hooks
import { useState, useCallback } from "react";
import { useMedplum } from "@medplum/react";

import { ensureActiveSession } from "@/utils/auth/ensureActiveSession";
import { getApiEndpoint, getApiAuthHeaders } from "@/utils/api/getApiEndpoint";
import type {
  ExtractedPatientInfo,
  PotentialDuplicate,
  ReferralExtractResult,
} from "@benchmark/core/types";

// Phase 5e consolidated `ExtractedPatientInfo`, `PotentialDuplicate`,
// and `ReferralExtractResult` to `@benchmark/core/types/domain/extracted-patient-info`.
// Re-exported here so existing consumers that imported from this hook file
// keep working until they are migrated to import directly from core.
export type { ExtractedPatientInfo, PotentialDuplicate, ReferralExtractResult };

/**
 * Result type for useExtractReferralPdf hook.
 * @agentic connections
 */
export type UseExtractReferralPdfResult = {
  /** Extract patient info from a PDF file */
  extractReferral: (file: File) => Promise<ReferralExtractResult>;
  /** Extract patient info from a Dialpad fax URL */
  extractFromUrl: (url: string) => Promise<ReferralExtractResult>;
  isExtracting: boolean;
  error: Error | null;
};

/**
 * Referral extract API path.
 * @agentic connections
 */
const REFERRAL_EXTRACT_PATH = "/api/v1/referral/extract";

/**
 * Dialpad fax URL patterns for validation (without query params).
 * @agentic connections
 */
const DIALPAD_FAX_URL_PATTERNS = [
  // Legacy format: /blob/fax/<id>
  /^https:\/\/dialpad\.com\/blob\/fax\/[a-zA-Z0-9_-]+$/,
  // New format: /blob-server/d/<base64-encoded-token>
  /^https:\/\/dialpad\.com\/blob-server\/d\/[a-zA-Z0-9_-]+=*$/,
];

/**
 * Cleans a Dialpad URL by removing query parameters.
 * @agentic connections
 */
const cleanDialpadUrl = (url: string): string => {
  try {
    const urlObj = new URL(url);
    return `${urlObj.origin}${urlObj.pathname}`;
  } catch {
    return url;
  }
};

/**
 * Validates a Dialpad fax URL (strips query params before validation).
 * @agentic connections
 */
const isValidDialpadFaxUrl = (url: string): boolean => {
  const cleanedUrl = cleanDialpadUrl(url);
  return DIALPAD_FAX_URL_PATTERNS.some((pattern) => pattern.test(cleanedUrl));
};

/**
 * Extracts patient info from referral PDFs or Dialpad fax URLs via backend API.
 * Uses Medplum token for auth; returns extracted info and Binary reference.
 * @agentic connections
 */
export const useExtractReferralPdf = (): UseExtractReferralPdfResult => {
  const medplum = useMedplum();
  const [isExtracting, setIsExtracting] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const extractReferral = useCallback(
    async (file: File): Promise<ReferralExtractResult> => {
      // Validate file type
      if (file.type !== "application/pdf") {
        throw new Error("Only PDF files are supported");
      }

      // Ensure session is active and get a fresh access token
      const accessToken = await ensureActiveSession(medplum);

      setIsExtracting(true);
      setError(null);

      try {
        // Read file as ArrayBuffer
        const arrayBuffer = await file.arrayBuffer();
        const pdfBytes = new Uint8Array(arrayBuffer);

        // Convert to base64 for JSON transport
        // Note: We use chunked conversion to avoid stack overflow with large files
        // The spread operator (...pdfBytes) would exceed call stack for large PDFs
        let binary = "";
        const chunkSize = 8192; // Process in 8KB chunks to avoid stack overflow
        for (let i = 0; i < pdfBytes.length; i += chunkSize) {
          const chunk = pdfBytes.subarray(i, i + chunkSize);
          binary += String.fromCharCode.apply(null, Array.from(chunk));
        }
        const base64 = btoa(binary);

        // Get the API endpoint URL
        const apiUrl = getApiEndpoint(REFERRAL_EXTRACT_PATH);

        // Call the extraction API
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 120000); // 2 minute timeout

        const response = await fetch(apiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Medplum-Authorization": `Bearer ${accessToken}`,
            ...getApiAuthHeaders(),
          },
          body: JSON.stringify({
            file: base64,
            filename: file.name,
          }),
          signal: controller.signal,
        }).finally(() => clearTimeout(timeout));

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(
            errorData.error ||
              `Failed to extract referral: ${response.status} ${response.statusText}`,
          );
        }

        const result = (await response.json()) as ReferralExtractResult;

        setIsExtracting(false);
        return result;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Failed to extract referral. Please try again.";

        const errorObj = new Error(errorMessage);
        setError(errorObj);
        setIsExtracting(false);
        throw errorObj;
      }
    },
    [medplum],
  );

  const extractFromUrl = useCallback(
    async (url: string): Promise<ReferralExtractResult> => {
      // Validate URL format
      if (!isValidDialpadFaxUrl(url)) {
        throw new Error(
          "Invalid URL. Only Dialpad fax URLs are supported (https://dialpad.com/blob/fax/... or https://dialpad.com/blob-server/d/...).",
        );
      }

      // Ensure session is active and get a fresh access token
      const accessToken = await ensureActiveSession(medplum);

      setIsExtracting(true);
      setError(null);

      try {
        // Get the API endpoint URL
        const apiUrl = getApiEndpoint(REFERRAL_EXTRACT_PATH);

        // Call the extraction API with URL
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 120000); // 2 minute timeout

        const response = await fetch(apiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Medplum-Authorization": `Bearer ${accessToken}`,
            ...getApiAuthHeaders(),
          },
          body: JSON.stringify({
            url,
          }),
          signal: controller.signal,
        }).finally(() => clearTimeout(timeout));

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(
            errorData.error ||
              `Failed to extract referral: ${response.status} ${response.statusText}`,
          );
        }

        const result = (await response.json()) as ReferralExtractResult;

        setIsExtracting(false);
        return result;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Failed to extract referral from URL. Please try again.";

        const errorObj = new Error(errorMessage);
        setError(errorObj);
        setIsExtracting(false);
        throw errorObj;
      }
    },
    [medplum],
  );

  return {
    extractReferral,
    extractFromUrl,
    isExtracting,
    error,
  };
};
